import React, { useEffect } from 'react'
import './styles/custom-style.css'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'

import { LanguageProvider } from './contexts/LanguageContext'
import { WebsiteProvider } from './contexts/WebsiteContext'
import { CartProvider } from './contexts/CartContext'

// eslint-disable-next-line
import i18n from './config/i18n'

// Components
import SiteHeader from './components/SiteHeader/SiteHeader'
import ScrollToTop from './utils/ScrollToTop'
import { AuthProvider } from './contexts/AuthContext'
import CookieConsentBanner from './components/Footer/CookieConsentBanner'
import PageTitleUpdater from './components/PageTitleUpdater'

// Main pages
import Home from './pages/Home'
import PointsOfInterest from './pages/PointsOfInterest'
import PointOfInterest from './pages/PointOfInterest'
import Events from './pages/Events'

// Manage pages
import ManagePoiEdit from './pages/ManagePoiEdit'
import ManagePoiExternalLinks from './pages/ManagePoiExternalLinks'
import ManagePoiInternalLinks from './pages/ManagePoiInternalLinks'
import ManagePoiPhotos from './pages/ManagePoiPhotos'
import ManageBeforeAfters from './pages/ManageBeforeAfters'

// Admin pages
import ManageContacts from './pages/ManageContacts'
import ManageComments from './pages/ManageComments'
import ManageExternalLinks from './pages/ManageExternalLinks'
import ManageExternalLinkEdit from './pages/ManageExternalLinkEdit'
import ManageExternalLink from './pages/ManageExternalLink'

// Other pages
import Contact from './pages/Contact'
import Support from './pages/Support'
import FAQ from './pages/FAQ'
import LegalNotices from './pages/LegalNotices'
import CGU from './pages/CGU'
import PageNotFound from './pages/PageNotFound'
import Login from './pages/Login'
import Register from './pages/Register'
import RegisterConfirmation from './pages/RegisterConfirmation'
import RegisterValidation from './pages/RegisterValidation'
import ForgottenPassword from './pages/ForgottenPassword'
import ForgottenPasswordRequest from './pages/ForgottenPasswordRequest'

// POI type pages (to factorize with PoiTypePage)
import PoiTypePage from './pages/PoiTypePage'
import VillageList from './components/PointOfinterestType/VillageList'
import AccomodationList from './components/PointOfinterestType/AccomodationList'
import PicnicList from './components/PointOfinterestType/PicnicList'
import WaterPointList from './components/PointOfinterestType/WaterPointList'
import RampageList from './components/PointOfinterestType/RampageList'
import WalkList from './components/PointOfinterestType/WalkList'
import WildlifeList from './components/PointOfinterestType/WildlifeList'
import HistoryList from './components/PointOfinterestType/HistoryList'

// Shop
import Cart from './pages/Cart'
import OrderInformations from './pages/OrderInformations'
import ShopProduct from './pages/ShopProduct'
import Shop from './pages/Shop'
import ShopManageProductPhotos from './pages/ShopManageProductPhotos'
import OurTeam from './pages/OurTeam'

import {
  HOME_PATH,
  POIS_PATH,
  POI_PATH,
  EVENTS_PATH,
  EVENT_PATH,
  MANAGE_POI_EDIT_PATH,
  MANAGE_POI_NEW_PATH,
  MANAGE_EVENT_EDIT_PATH,
  MANAGE_EVENT_NEW_PATH,
  MANAGE_POI_PHOTOS_PATH,
  MANAGE_BEFORE_AFTERS_PATH,
  MANAGE_POI_INTERNAL_LINKS_PATH,
  MANAGE_POI_EXTERNAL_LINKS_PATH,
  CONTACT_PATH,
  SUPPORT_PATH,
  FAQ_PATH,
  LEGAL_NOTICES_PATH,
  TERMS_OF_USE_PATH,
  LOGIN_PATH,
  REGISTER_PATH,
  REGISTER_CONFIRMATION_PATH,
  REGISTER_VALIDATION_PATH,
  FORGOTTEN_PASSWORD_REQUEST_PATH,
  FORGOTTEN_PASSWORD_PATH,
  ACCOMODATION_PATH,
  MOUNTAIN_BIKING_PATH,
  VILLAGE_PATH,
  WILDLIFE_PATH,
  HISTORY_PATH,
  ROAD_CYCLING_PATH,
  TRAIL_PATH,
  HIKING_PATH,
  PICNIC_PATH,
  WATER_POINT_PATH,
  RAMPAGE_PATH,
  CURIOSITY_PATH,
  MANAGE_CONTACTS_PATH,
  MANAGE_COMMENTS_PATH,
  MANAGE_EXTERNAL_LINK_PATH,
  MANAGE_EXTERNAL_LINKS_PATH,
  MANAGE_EXTERNAL_LINK_NEW_PATH,
  MANAGE_EXTERNAL_LINK_EDIT_PATH,
  OUR_TEAM_PATH,
  SHOP_PATH,
  CART_PATH,
  ORDER_INFORMATIONS_PATH,
  PRODUCT_PATH,
  MANAGE_PRODUCT_PHOTOS_PATH,
  WALK_PATH,
  CLIMBING_PATH,
} from './config/routes'
import { PoisProvider } from './contexts/PoisContext'
import PoiTypeHiking from './pages/PoiTypeHiking'
import PoiTypeTrail from './pages/PoiTypeTrail'
import PoiTypeRoadCycling from './pages/PoiTypeRoadCycling'
import PoiTypeMountainBiking from './pages/PoiTypeMountainBiking'
import PoiTypeClimbing from './pages/PoiTypeClimbing'
import CuriosityList from './components/PointOfinterestType/CuriosityList'

function App() {
  // Manage onKeyDown (submit form)
  useEffect(() => {
    const handleEnterKeyPress = (event) => {
      if (event.key === 'Enter') {
        const focusedElement = document.activeElement
        if (
          focusedElement.tagName === 'INPUT' &&
          focusedElement.type !== 'file' &&
          focusedElement.tagName !== 'TEXTAREA'
        ) {
          const form = focusedElement.closest('form')
          if (form) {
            const submitEvent = new CustomEvent('submit', { cancelable: true })
            form.dispatchEvent(submitEvent)
          }
        }
      }
    }

    window.addEventListener('keydown', handleEnterKeyPress)

    return () => {
      window.removeEventListener('keydown', handleEnterKeyPress)
    }
  }, [])

  // Factorized POI routes with PoiTypePage and List components passed as prop
  const poiTypeRoutes = [
    { path: VILLAGE_PATH, poiType: 'VILLAGE', Component: VillageList },
    {
      path: ACCOMODATION_PATH,
      poiType: 'ACCOMODATION',
      Component: AccomodationList,
    },
    { path: PICNIC_PATH, poiType: 'PICNIC', Component: PicnicList },
    {
      path: WATER_POINT_PATH,
      poiType: 'WATER_POINT',
      Component: WaterPointList,
    },
    { path: RAMPAGE_PATH, poiType: 'RAMPAGE', Component: RampageList },
    { path: CURIOSITY_PATH, poiType: 'CURIOSITY', Component: CuriosityList },
    { path: WALK_PATH, poiType: 'WALK', Component: WalkList },
    { path: WILDLIFE_PATH, poiType: 'WILDLIFE', Component: WildlifeList },
    { path: HISTORY_PATH, poiType: 'HISTORY', Component: HistoryList },
  ]

  return (
    <WebsiteProvider>
      <LanguageProvider>
        <Router>
          <PageTitleUpdater />
          <AuthProvider>
            <PoisProvider>
              <CartProvider>
                <ScrollToTop />
                <div>
                  <SiteHeader />
                  <CookieConsentBanner />
                  <Routes>
                    <Route path={HOME_PATH} element={<Home />} />

                    <Route path={POIS_PATH} element={<PointsOfInterest />} />
                    <Route
                      path={POI_PATH}
                      element={<PointOfInterest isEvent={false} />}
                    />
                    <Route path={EVENTS_PATH} element={<Events />} />
                    <Route
                      path={EVENT_PATH}
                      element={<PointOfInterest isEvent={true} />}
                    />

                    <Route path={LOGIN_PATH} element={<Login />} />
                    <Route path={REGISTER_PATH} element={<Register />} />
                    <Route
                      path={REGISTER_CONFIRMATION_PATH}
                      element={<RegisterConfirmation />}
                    />
                    <Route
                      path={FORGOTTEN_PASSWORD_PATH}
                      element={<ForgottenPassword />}
                    />
                    <Route
                      path={FORGOTTEN_PASSWORD_REQUEST_PATH}
                      element={<ForgottenPasswordRequest />}
                    />
                    <Route
                      path={REGISTER_VALIDATION_PATH}
                      element={<RegisterValidation />}
                    />

                    <Route path={CONTACT_PATH} element={<Contact />} />
                    <Route path={SUPPORT_PATH} element={<Support />} />
                    <Route path={FAQ_PATH} element={<FAQ />} />
                    <Route
                      path={LEGAL_NOTICES_PATH}
                      element={<LegalNotices />}
                    />
                    <Route path={TERMS_OF_USE_PATH} element={<CGU />} />

                    {poiTypeRoutes.map(({ path, poiType, Component }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <PoiTypePage
                            Component={Component}
                            poiType={poiType}
                          />
                        }
                      />
                    ))}

                    {/* NON-factorized (excluded) POI types */}
                    <Route
                      path={MOUNTAIN_BIKING_PATH}
                      element={<PoiTypeMountainBiking />}
                    />
                    <Route
                      path={ROAD_CYCLING_PATH}
                      element={<PoiTypeRoadCycling />}
                    />
                    <Route path={HIKING_PATH} element={<PoiTypeHiking />} />
                    <Route path={CLIMBING_PATH} element={<PoiTypeClimbing />} />
                    <Route path={TRAIL_PATH} element={<PoiTypeTrail />} />

                    <Route
                      path={MANAGE_POI_EDIT_PATH}
                      element={<ManagePoiEdit isEvent={false} />}
                    />
                    <Route
                      path={MANAGE_POI_NEW_PATH}
                      element={<ManagePoiEdit isEvent={false} />}
                    />
                    <Route
                      path={MANAGE_EVENT_EDIT_PATH}
                      element={<ManagePoiEdit isEvent={true} />}
                    />
                    <Route
                      path={MANAGE_EVENT_NEW_PATH}
                      element={<ManagePoiEdit isEvent={true} />}
                    />
                    <Route
                      path={MANAGE_POI_PHOTOS_PATH}
                      element={<ManagePoiPhotos />}
                    />

                    <Route
                      path={MANAGE_BEFORE_AFTERS_PATH}
                      element={<ManageBeforeAfters />}
                    />

                    <Route
                      path={MANAGE_POI_INTERNAL_LINKS_PATH}
                      element={<ManagePoiInternalLinks />}
                    />
                    <Route
                      path={MANAGE_POI_EXTERNAL_LINKS_PATH}
                      element={<ManagePoiExternalLinks />}
                    />

                    <Route
                      path={MANAGE_CONTACTS_PATH}
                      element={<ManageContacts />}
                    />
                    <Route
                      path={MANAGE_COMMENTS_PATH}
                      element={<ManageComments />}
                    />
                    <Route
                      path={MANAGE_EXTERNAL_LINKS_PATH}
                      element={<ManageExternalLinks />}
                    />
                    <Route
                      path={MANAGE_EXTERNAL_LINK_NEW_PATH}
                      element={<ManageExternalLinkEdit />}
                    />
                    <Route
                      path={MANAGE_EXTERNAL_LINK_EDIT_PATH}
                      element={<ManageExternalLinkEdit />}
                    />
                    <Route
                      path={MANAGE_EXTERNAL_LINK_PATH}
                      element={<ManageExternalLink />}
                    />
                    <Route path={CART_PATH} element={<Cart />} />
                    <Route
                      path={ORDER_INFORMATIONS_PATH}
                      element={<OrderInformations />}
                    />
                    <Route path={SHOP_PATH} element={<Shop />} />
                    <Route path={OUR_TEAM_PATH} element={<OurTeam />} />
                    <Route path={PRODUCT_PATH} element={<ShopProduct />} />
                    <Route
                      path={MANAGE_PRODUCT_PHOTOS_PATH}
                      element={<ShopManageProductPhotos />}
                    />

                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </div>
              </CartProvider>
            </PoisProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </WebsiteProvider>
  )
}

export default App
